// Check against the plan: a draft PO compared with what the plan suggests now, the same in the browser (the PO
// panel's "Check against the plan" tab and the warning before sending) and on the server (the MCP's check_draft_po).
//
// A draft is often made days before it's sent, and stock, receipts, other POs and the forecast move meanwhile. The
// plan counts open POs as supply, drafts included, so this draft has already shrunk the plan's own suggestion for its
// items: each line is compared with what the plan would suggest for that item if this draft didn't exist
// (planWithout(): the plan again for just the draft's items, with its lines taken out of supply; lines on other open
// POs still count). Each item on the draft gets the plan's quantity now (rounded by this vendor's MOQ and multiple),
// a status (same / more needed / less needed / not needed now), the reasons in words (what changed since the line's
// plan_basis, the snapshot taken when it was drafted from the plan), the order-by date, and who set its quantity
// (auto mode's rule for auto drafts; else the basis). Items now due from this vendor to this ship-to that aren't on
// the draft are suggested additions. Rounding isn't a change (withinRounding()). Make POs compare their builds with
// the plan's builds the same way (the MRP plans builds like bought items, with the CM's terms).

import { delayText } from "./closures.ts";
import { hash53 } from "./hash.ts";
import {
  addPlanDays,
  type CoverTo,
  coverText,
  dayNum,
  explain,
  fmt,
  type ItemPlanResult,
  orderByText,
  type Plan,
  type PlanData,
  planPurchasing,
  shortDate,
} from "./planning.ts";
import { type PlanBasis, planBasisSchema, qtyWarnings, type VendorTerms, validQty } from "./po.ts";

type Time = string | Date | null | undefined;

/** The PO fields the check reads (synced rows in the browser, rows from Postgres on the server). */
export type CheckPo = {
  id: string;
  number: string;
  vendor_id: string;
  status: string;
  kind: string;
  ship_to_location_id: string | null;
  auto_key?: string;
  auto_run_at?: Time;
  auto_removed?: readonly string[];
  /** Cover through --- the day it was made to last to (migration 061); the plan it's checked against covers to then. */
  cover_through?: Time;
};
export type CheckLine = {
  id: string;
  po_id: string;
  item_id: string;
  qty: number;
  sku: string;
  name: string;
  position?: number;
  auto_qty?: number | null;
  updated_at?: Time;
  plan_basis?: unknown;
};

/**
 * A change in the need smaller than this share of it isn't a difference (the forecast moves a little every night).
 * Differences that only the MOQ or order multiple make aren't either: see withinRounding().
 */
export const CHECK_TOLERANCE_PCT = 5;

export type LineStatus = "same" | "more" | "less" | "not_needed" | "unplanned";
export const LINE_STATUS_LABELS: Record<LineStatus, string> = {
  same: "Same",
  more: "More needed",
  less: "Less needed",
  not_needed: "Not needed now",
  unplanned: "Not planned",
};
/**
 * Who set a line's quantity: auto mode, the plan (Create draft POs), the check's Update, or a person; legacy = drafted
 * before lines kept the plan's numbers (migration 024), so who and why aren't known.
 */
export type LineSource = "auto" | "plan" | "check" | "changed" | "added" | "legacy";

export type CheckRow = {
  itemId: string;
  sku: string;
  name: string;
  /** Its lines on the draft (almost always one; the row is the item's total), with each one's own qty as the check saw it. */
  lineIds: string[];
  lines: { id: string; qty: number }[];
  drafted: number;
  /** The plan's quantity now without this draft, rounded by this vendor's MOQ and multiple; null = not planned. */
  plan: number | null;
  /** plan − drafted. */
  diff: number;
  status: LineStatus;
  /** Why, in words: what changed since it was drafted, or what the plan sees now. */
  reasons: string[];
  /** The plan's order-by date (YYYY-MM-DD) and the same in words ("Now (3 days late)", "Nov 20"). */
  orderBy: string | null;
  orderByText: string;
  source: LineSource;
  sourceText: string;
  /** A person typed or added it (auto mode's rule for its own drafts): not ticked for an update by default. */
  byPerson: boolean;
  /** What Update selected does with it: set its quantity to the plan's, take it off, or nothing. */
  action: "set" | "remove" | null;
  /** Ticked by default: a real difference on a line the plan or auto mode set. */
  tick: boolean;
  /** The newest snapshot of its lines (null = added by hand, or drafted before snapshots). */
  basis: PlanBasis | null;
  result: ItemPlanResult | null;
};

export type CheckAddition = {
  itemId: string;
  sku: string;
  name: string;
  qty: number;
  orderBy: string | null;
  orderByText: string;
  reasons: string[];
  /** Taken off this auto draft by a person before (auto mode doesn't put it back): not ticked by default. */
  removedBefore: boolean;
  tick: boolean;
  result: ItemPlanResult;
};

export type DraftCheck = {
  /** False for POs the check isn't for (sent ones, services): `why` says so. */
  applies: boolean;
  why: string;
  today: string;
  /** The plan without this draft (its items replanned), for explain(). */
  plan: Plan | null;
  rows: CheckRow[];
  additions: CheckAddition[];
  /** Rows that differ (more, less, not needed now) and items due that aren't on it. */
  differences: number;
  missing: number;
  /** A fingerprint of the differences, so a send doesn't warn again about the ones staff already decided on. */
  key: string;
  /** "3 lines differ from the plan since this was drafted; 1 item due from Tailhoo isn't on it" ('' = nothing). */
  warning: string;
};

const r4 = (n: number) => Math.round(n * 1e4) / 1e4;
const time = (t: Time) => (t == null ? null : (t instanceof Date ? t : new Date(t)).getTime());
const dayText = (t: Time) => (t == null ? null : typeof t === "string" ? t.slice(0, 10) : t.toISOString().slice(0, 10));
const near = (a: number, b: number) => Math.abs(a - b) < 1e-9;

/** The snapshot a line keeps of the plan (po_line.plan_basis), when it's drafted or updated from a plan result. */
export function planBasis(r: ItemPlanResult, today: string, opts: { via: PlanBasis["via"]; qty?: number; poId?: string }): PlanBasis {
  return {
    via: opts.via,
    at: today,
    qty: r4(Math.max(0, opts.qty ?? r.qty)),
    raw: r4(Math.max(0, r.rawQty)),
    available: r4(r.available),
    incoming: r.incoming
      .filter((x) => x.poId !== opts.poId)
      .slice(0, 20)
      .map((x) => ({ po: x.poNumber.slice(0, 40), qty: r4(x.qty) })),
    rate: r4(Math.max(0, r.rate)),
    b2b: r4(r.b2bOpen),
    dependent: r4(r.dependentQty),
    order_by: r.orderByDay === null ? null : addPlanDays(today, r.orderByDay),
    safety: Math.ceil(r.safetyStock),
    moq: r.moq,
    multiple: r.multiple,
    lead: r.leadTime,
    closed: r.delay?.days ?? 0,
  };
}

/** Lines from before migration 024 carry {"legacy": true}: drafted before lines kept the plan's numbers. */
export const isLegacyBasis = (raw: unknown) => !!raw && typeof raw === "object" && (raw as { legacy?: unknown }).legacy === true;

/** A line's snapshot, or null for a line a person added (or one from before snapshots). */
export function basisOf(raw: unknown): PlanBasis | null {
  if (!raw || typeof raw !== "object" || !("at" in raw)) return null;
  const parsed = planBasisSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/** Cover through --- a draft's own day to buy to as the plan's (for every vendor: the draft is one vendor's). */
export function draftCover(po: Pick<CheckPo, "cover_through">, today: string): CoverTo | undefined {
  const day = dayText(po.cover_through);
  return day ? { all: dayNum(day) - dayNum(today), byVendor: new Map() } : undefined;
}

/**
 * The plan as if this draft didn't exist: its lines (and their make orders) are taken out of supply and only its items
 * are planned again (items in BOMs always are). With `prev` (the plan of the same data) the rest is reused. Cover
 * through --- `data.coverTo` is the draft's (draftCover()): with a cover-through day on either side, its vendor's items
 * are planned again too, so what's due from that vendor is what the draft was meant to cover, not the viewer's own day.
 */
export function planWithout(data: PlanData, po: string | Pick<CheckPo, "id" | "vendor_id">, prev?: Plan): Plan {
  const poId = typeof po === "string" ? po : po.id;
  const mine = data.poLines.filter((l) => l.po_id === poId);
  const only = new Set(mine.map((l) => l.item_id));
  if (prev && typeof po !== "string") for (const r of prev.results) if (r.vendor?.id === po.vendor_id && (data.coverTo || r.coverTo)) only.add(r.item.id);
  if (!only.size && prev) return prev;
  const ids = new Set(mine.map((l) => l.id));
  const without: PlanData = {
    ...data,
    poLines: data.poLines.filter((l) => l.po_id !== poId),
    ...(data.makeOrders ? { makeOrders: data.makeOrders.filter((m) => !ids.has(m.po_line_id)) } : {}),
  };
  return prev ? planPurchasing(without, { prev, only }) : planPurchasing(without);
}

/**
 * Whether a drafted quantity matches the plan's need once rounding is allowed for. Never when the vendor wouldn't take
 * it (below the MOQ, not a whole number of order multiples). Otherwise the same when: it's the plan's quantity; it
 * covers the need with less than one order multiple to spare (what a multiple rounds up to); it's within
 * CHECK_TOLERANCE_PCT of the need; or the lines are still what the plan drafted (`draftedNeed`: the need then) and the
 * need has moved less than that since (so the need crossing a multiple, which moves the rounded quantity a whole step,
 * isn't a change).
 */
export function withinRounding(drafted: number, need: number, plan: number, terms: VendorTerms | undefined, draftedNeed: number | null): boolean {
  if (terms && qtyWarnings(terms, drafted).length) return false;
  if (near(drafted, plan)) return true;
  const step = terms && Number(terms.order_multiple) > 0 ? Number(terms.order_multiple) : 1;
  const tol = Math.max(1, (CHECK_TOLERANCE_PCT / 100) * need);
  if (drafted - step < need && need <= drafted + 1e-9) return true;
  if (Math.abs(need - drafted) <= tol) return true;
  return draftedNeed !== null && Math.abs(need - draftedNeed) <= tol;
}

/**
 * A short stable fingerprint (cyrb53) of the differences: item, status, the plan's quantity and what's drafted, so an
 * edit after "Keep as they are" brings the warning back.
 */
export function checkKey(
  rows: readonly Pick<CheckRow, "itemId" | "status" | "plan" | "drafted">[],
  additions: readonly Pick<CheckAddition, "itemId" | "qty">[],
): string {
  const parts = [
    ...rows.filter((r) => isDifference(r.status)).map((r) => `${r.itemId}:${r.status}:${r.plan ?? ""}:${r.drafted}`),
    ...additions.map((a) => `${a.itemId}:add:${a.qty}`),
  ].sort();
  if (!parts.length) return "";
  return hash53(parts.join("|"));
}

export const isDifference = (s: LineStatus) => s === "more" || s === "less" || s === "not_needed";

const notApplicable = (why: string, today: string): DraftCheck => ({
  applies: false,
  why,
  today,
  plan: null,
  rows: [],
  additions: [],
  differences: 0,
  missing: 0,
  key: "",
  warning: "",
});

const plural = (n: number, one: string, many: string) => `${fmt(n)} ${n === 1 ? one : many}`;

/** Checks a draft against the plan of `data` (with `plan`, that data's plan, only the draft's items are replanned). */
export function checkDraft(po: CheckPo, lines: readonly CheckLine[], data: PlanData, opts: { plan?: Plan } = {}): DraftCheck {
  const today = data.today;
  if (po.status !== "draft") return notApplicable(`${po.number} has gone to the vendor: the check is for drafts, before they're sent.`, today);
  if (po.kind === "service") return notApplicable("Service POs aren't planned, so there's nothing to check them against.", today);
  const plan = planWithout({ ...data, coverTo: draftCover(po, today) }, po, opts.plan);
  const review = plan.settings.review_days;
  const vendor = data.vendors.find((v) => v.id === po.vendor_id);
  const vendorName = vendor ? vendor.short_name || vendor.name : "this vendor";
  const placeName = new Map(data.locations.map((l) => [l.id, l.name ?? ""]));
  const linkOf = new Map(data.vendorItems.filter((l) => l.vendor_id === po.vendor_id).map((l) => [l.item_id, l]));
  const autoRun = time(po.auto_run_at);
  const date = (iso: string) => shortDate(iso, today);

  /** This vendor's terms for an item: the plan's when the plan buys it here, else this vendor's link (its MOQ, else the vendor's). */
  const termsFor = (itemId: string, r: ItemPlanResult): VendorTerms | undefined => {
    if (r.vendor?.id === po.vendor_id && r.terms) return r.terms;
    const link = linkOf.get(itemId);
    if (!link) return undefined;
    const moq = Number(link.moq) > 0 ? Number(link.moq) : Number(vendor?.moq ?? 0) > 0 ? Number(vendor?.moq) : 0;
    return { ...link, moq };
  };

  const sourceOf = (l: CheckLine, basis: PlanBasis | null): LineSource => {
    // Auto mode's own drafts: a line is its own while auto_qty = qty and nothing changed it since the last run.
    if (po.auto_key && l.auto_qty != null) {
      const untouched = near(Number(l.qty), Number(l.auto_qty)) && autoRun !== null && (time(l.updated_at) ?? Number.POSITIVE_INFINITY) <= autoRun;
      return untouched ? "auto" : "changed";
    }
    if (basis) {
      if (!near(Number(l.qty), basis.qty)) return "changed";
      return basis.via === "check" ? "check" : basis.via === "auto" ? "auto" : "plan";
    }
    // Made by auto mode (on a draft that's no longer its own): auto-made unless someone changed the qty since.
    if (l.auto_qty != null) return near(Number(l.qty), Number(l.auto_qty)) ? "auto" : "changed";
    return isLegacyBasis(l.plan_basis) ? "legacy" : "added";
  };

  const byItem = new Map<string, CheckLine[]>();
  for (const l of [...lines].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))) {
    const list = byItem.get(l.item_id);
    if (list) list.push(l);
    else byItem.set(l.item_id, [l]);
  }

  const rows: CheckRow[] = [];
  for (const [itemId, ls] of byItem) {
    const drafted = ls.reduce((n, l) => n + Number(l.qty), 0);
    // The newest snapshot (an update through the check sets the item's last line).
    const bases = ls.map((l) => basisOf(l.plan_basis));
    const basis = bases.reduce<PlanBasis | null>((best, b) => (b && (!best || b.at >= best.at) ? b : best), null);
    const sources = ls.map((l, k) => sourceOf(l, bases[k]));
    // The item reads as its most telling line: a person's change, then an update through the check.
    const source = (["changed", "added", "legacy", "check", "auto"] as const).find((x) => sources.includes(x)) ?? sources[0];
    const r = plan.byItem.get(itemId) ?? null;
    const row: CheckRow = {
      itemId,
      sku: ls[0].sku,
      name: ls[0].name,
      lineIds: ls.map((l) => l.id),
      lines: ls.map((l) => ({ id: l.id, qty: Number(l.qty) })),
      drafted,
      plan: null,
      diff: 0,
      status: "unplanned",
      reasons: [],
      orderBy: null,
      orderByText: "",
      source,
      sourceText:
        source === "auto"
          ? "Auto mode's"
          : source === "plan"
            ? "From the plan"
            : source === "check"
              ? "Updated from the plan"
              : source === "changed"
                ? basis
                  ? `Changed by hand (the plan said ${fmt(basis.qty)})`
                  : "Changed by hand"
                : source === "legacy"
                  ? "Drafted before snapshots"
                  : "Added by hand",
      byPerson: source === "changed" || source === "added",
      action: null,
      tick: false,
      basis,
      result: r,
    };
    rows.push(row);
    if (!r) {
      row.reasons = ["The plan doesn't plan this item: it plans bought sell items, and the builds and parts in BOMs."];
      continue;
    }
    if (!r.planned) {
      row.reasons = [r.skip];
      continue;
    }
    const terms = termsFor(itemId, r);
    const need = r.rawQty;
    const planQty = need > 0 ? (r.vendor?.id === po.vendor_id ? r.qty : validQty(terms, need)) : 0;
    // Cover through --- running short before the draft's day makes it due too.
    const due = (r.orderByDay !== null && r.orderByDay <= review) || !!r.coverTo?.short;
    const wasDue = basis ? basis.order_by !== null && dayNum(basis.order_by) - dayNum(basis.at) <= review : null;
    row.plan = planQty;
    row.diff = r4(planQty - drafted);
    row.orderBy = r.orderByDay === null ? null : addPlanDays(today, r.orderByDay);
    row.orderByText = orderByText(r, today);
    // The need when drafted counts only while the lines are still what the plan set and add up to what it wanted then.
    const asDrafted =
      basis && sources.every((x) => x === "plan" || x === "auto" || x === "check") && near(drafted, validQty(terms, basis.raw)) ? basis.raw : null;
    if (planQty <= 0 || (!due && wasDue !== false)) row.status = "not_needed";
    else if (withinRounding(drafted, need, planQty, terms, asDrafted)) row.status = "same";
    else row.status = planQty > drafted ? "more" : "less";
    if (row.status === "same") row.diff = 0;
    row.reasons = reasonsFor(r, basis, row, { today, poId: po.id, planQty, date, source });
    if (r.vendor && r.vendor.id !== po.vendor_id) row.reasons.push(`The plan buys it from ${r.vendor.short_name || r.vendor.name}.`);
    if (po.ship_to_location_id && r.shipTo && r.shipTo !== po.ship_to_location_id) {
      row.reasons.push(`The plan sends it to ${placeName.get(r.shipTo) || "another place"}, not where this PO ships.`);
    }
    if (r.bucket === "snoozed") row.reasons.push(`Snoozed until ${date(r.snoozedUntil as string)}${r.snoozeReason ? `: ${r.snoozeReason}` : ""}.`);
    // Update sets the item's last line so the total is the plan's (it can't if that leaves it at nothing), or takes it off.
    const last = ls[ls.length - 1];
    if (row.status === "not_needed") row.action = "remove";
    else if (row.status === "more" || row.status === "less") row.action = Number(last.qty) + (planQty - drafted) > 0 ? "set" : null;
    // Pre-ticked only where the plan or auto mode set the quantity (a person's, or a line from before snapshots, aren't).
    row.tick = row.action !== null && (source === "plan" || source === "auto" || source === "check");
  }

  // Due from this vendor to this ship-to, not on the draft (the plan's own "order now": order-by within the review period).
  const additions: CheckAddition[] = [];
  const kind = po.kind === "make" ? "make" : "buy";
  for (const r of plan.results) {
    if (byItem.has(r.item.id)) continue;
    if (!r.planned || r.bucket === "snoozed" || r.qty <= 0 || r.orderByDay === null || (r.orderByDay > review && !r.coverTo?.short)) continue;
    if (r.vendor?.id !== po.vendor_id || r.vendor.archived || r.role !== kind) continue;
    if (po.ship_to_location_id && r.shipTo && r.shipTo !== po.ship_to_location_id) continue;
    const removedBefore = !!po.auto_removed?.includes(r.item.id);
    const reasons = [
      `Due: order ${r.orderByDay <= 0 ? "now" : `by ${date(addPlanDays(today, r.orderByDay))}`}; ${fmt(r.available)} available, ${coverText(r)} of cover${r.incomingQty > 0 ? `, ${fmt(r.incomingQty)} on other POs` : ""}.`,
    ];
    if (removedBefore) reasons.push("Someone took it off this draft before, so auto mode leaves it off.");
    additions.push({
      itemId: r.item.id,
      sku: r.item.sku,
      name: r.item.name,
      qty: r.qty,
      orderBy: addPlanDays(today, r.orderByDay),
      orderByText: orderByText(r, today),
      reasons,
      removedBefore,
      tick: !removedBefore,
      result: r,
    });
  }
  additions.sort((a, b) => (a.result.orderByDay ?? 0) - (b.result.orderByDay ?? 0) || a.sku.localeCompare(b.sku));

  const differences = rows.filter((r) => isDifference(r.status)).length;
  const missing = additions.length;
  const warning = [
    differences ? `${plural(differences, "line differs", "lines differ")} from the plan since this was drafted` : "",
    missing ? `${plural(missing, "item", "items")} due from ${vendorName} ${missing === 1 ? "isn't" : "aren't"} on it` : "",
  ]
    .filter(Boolean)
    .join("; ");
  return { applies: true, why: "", today, plan, rows, additions, differences, missing, key: checkKey(rows, additions), warning };
}

/** What changed since the line was drafted (its basis), or what the plan sees now for a line added by hand. */
function reasonsFor(
  r: ItemPlanResult,
  basis: PlanBasis | null,
  row: CheckRow,
  ctx: { today: string; poId: string; planQty: number; date: (iso: string) => string; source: LineSource },
): string[] {
  const { today, date } = ctx;
  const out: string[] = [];
  const orderBy = row.orderBy;
  if (row.status === "not_needed") {
    if (ctx.planQty <= 0) {
      out.push(
        r.rate + r.depRate > 0 || r.b2bOpen > 0
          ? `Not needed in the plan's next ${fmt(r.horizon)} days: ${coverText(r)} of cover.`
          : "Nothing is forecast to use it.",
      );
    } else {
      const was = basis?.order_by && basis.order_by !== orderBy ? ` (it was ${date(basis.order_by)} when drafted)` : "";
      out.push(`Not due yet: order by ${date(orderBy as string)}${was}.`);
    }
  }
  if (!basis && ctx.source !== "added") {
    out.push(
      ctx.source === "legacy"
        ? "Drafted before HHL Ops kept the plan's numbers on PO lines, so there's no why from then."
        : "Drafted by auto mode before HHL Ops kept the plan's numbers on PO lines, so there's no why from then.",
    );
    return out;
  }
  if (!basis) {
    const parts = [`${fmt(r.available)} available`];
    if (r.incomingQty > 0) parts.push(`${fmt(r.incomingQty)} on other POs`);
    if (r.rate > 0) parts.push(`forecast ${fmt(r.rate)}/day`);
    if (r.b2bOpen > 0) parts.push(`${fmt(r.b2bOpen)} wholesale to ship`);
    if (r.dependentQty > 0) parts.push(`${fmt(Math.round(r.dependentQty))} for builds`);
    out.push(`Added by hand, so there's no plan to compare with from then. Now: ${parts.join(", ")}${orderBy ? `; order by ${date(orderBy)}` : ""}.`);
    return out;
  }

  // Stock, receipts and other POs.
  const sum = (list: { po: string; qty: number }[]) => {
    const m = new Map<string, number>();
    for (const x of list) m.set(x.po, (m.get(x.po) ?? 0) + x.qty);
    return m;
  };
  const now = sum(r.incoming.filter((x) => x.poId !== ctx.poId).map((x) => ({ po: x.poNumber, qty: x.qty })));
  const then = sum(basis.incoming);
  const gone = [...then].filter(([po, q]) => (now.get(po) ?? 0) < q - 1e-9).map(([po, q]) => ({ po, qty: q - (now.get(po) ?? 0) }));
  const added = [...now].filter(([po, q]) => q > (then.get(po) ?? 0) + 1e-9).map(([po, q]) => ({ po, qty: q - (then.get(po) ?? 0), isNew: !then.has(po) }));
  const goneText = gone.map((g) => `${g.po} (${fmt(r4(g.qty))})`).join(", ");
  const dStock = r4(r.available - basis.available);
  if (gone.length && dStock > 0) {
    out.push(`A receipt landed: ${goneText} ${gone.length === 1 ? "is" : "are"} no longer on order, and stock is ${fmt(dStock)} higher than when drafted.`);
  } else {
    if (Math.abs(dStock) >= 0.5) {
      out.push(
        `Stock is ${fmt(Math.abs(dStock))} ${dStock > 0 ? "higher" : "lower"} than when drafted (${fmt(basis.available)} → ${fmt(r.available)})${dStock < 0 ? ": sold or used since" : ""}.`,
      );
    }
    if (gone.length) out.push(`${goneText} ${gone.length === 1 ? "is" : "are"} no longer on order.`);
  }
  for (const a of added) out.push(a.isNew ? `Now on ${a.po} (${fmt(r4(a.qty))}).` : `${fmt(r4(a.qty))} more on ${a.po}.`);

  // Demand.
  if (basis.rate > 0 && r.rate > 0) {
    const change = r.rate / basis.rate - 1;
    if (Math.abs(change) >= 0.03) {
      out.push(`The forecast went ${change > 0 ? "up" : "down"} ${Math.round(Math.abs(change) * 100)}% (${fmt(basis.rate)} → ${fmt(r.rate)}/day).`);
    }
  } else if (basis.rate === 0 && r.rate > 0) out.push(`It has a forecast now (${fmt(r.rate)}/day); it had none when drafted.`);
  else if (basis.rate > 0 && r.rate === 0) out.push(`Its forecast is 0 now (it was ${fmt(basis.rate)}/day).`);
  const dB2b = r4(r.b2bOpen - basis.b2b);
  if (Math.abs(dB2b) >= 0.5)
    out.push(`Wholesale/B2B orders: ${fmt(Math.abs(dB2b))} ${dB2b > 0 ? "more" : "fewer"} to ship (${fmt(basis.b2b)} → ${fmt(r.b2bOpen)}).`);
  const dDep = r4(r.dependentQty - basis.dependent);
  if (Math.abs(dDep) >= 0.5) out.push(`Builds need ${fmt(Math.round(Math.abs(dDep)))} ${dDep > 0 ? "more" : "fewer"} over the next year.`);

  // Terms and settings.
  if (r.moq !== basis.moq) out.push(`The MOQ is ${fmt(r.moq)} now (was ${fmt(basis.moq)}).`);
  if (r.multiple !== basis.multiple) out.push(`The order multiple is ${fmt(r.multiple)} now (was ${fmt(basis.multiple)}).`);
  if (r.leadTime !== basis.lead) out.push(`${r.make ? "Build" : "Lead"} time is ${r.leadTime} days now (was ${basis.lead}).`);
  // Vendor closures --- a closure added, moved or taken away since it was drafted.
  const closed = r.delay?.days ?? 0;
  if (closed !== (basis.closed ?? 0)) {
    out.push(
      r.delay
        ? `${delayText(r.delay)} now; ${basis.closed ? `it was ${basis.closed} day${basis.closed === 1 ? "" : "s"}` : "no closure was counted"} when drafted.`
        : `No vendor closure is in its lead time now (one added ${basis.closed} day${basis.closed === 1 ? "" : "s"} when drafted).`,
    );
  }
  const safety = Math.ceil(r.safetyStock);
  if (Math.abs(safety - basis.safety) >= Math.max(1, 0.05 * basis.safety)) out.push(`Safety stock is ${fmt(safety)} now (was ${fmt(basis.safety)}).`);

  // Timing (a not-needed line already says when; two dates both past are both "now").
  const t0 = dayNum(today);
  if (row.status !== "not_needed" && basis.order_by !== orderBy) {
    if (orderBy && basis.order_by) {
      if (dayNum(orderBy) > t0 || dayNum(basis.order_by) > t0) out.push(`Order-by moved from ${date(basis.order_by)} to ${date(orderBy)}.`);
    } else if (orderBy) out.push(`It needs ordering now: by ${date(orderBy)}.`);
  }
  if (!out.length) {
    const days = dayNum(today) - dayNum(basis.at);
    out.push(
      row.status === "same"
        ? days > 0
          ? `No change that matters since it was drafted ${days} day${days === 1 ? "" : "s"} ago.`
          : "No change since it was drafted."
        : `The plan has moved on ${days} day${days === 1 ? "" : "s"} since it was drafted (today's forecast and dates).`,
    );
  }
  return out;
}

/** The plan's full why for a row or an addition (explain()). */
export function whyOf(check: DraftCheck, r: ItemPlanResult | null): string[] {
  return r && check.plan ? explain(r, check.plan) : [];
}

/** What staff saw when they sent anyway: the warning and the first few differences, for the PO's record. */
export function overrideNote(check: DraftCheck): string {
  const lines = check.rows
    .filter((r) => isDifference(r.status))
    .map((r) => `${r.sku || r.name}: ${fmt(r.drafted)} drafted, the plan says ${fmt(r.plan ?? 0)} (${LINE_STATUS_LABELS[r.status].toLowerCase()})`);
  const adds = check.additions.map((a) => `${a.sku || a.name}: due (${fmt(a.qty)}), not on it`);
  const all = [...lines, ...adds];
  const shown = all.slice(0, 8).join("; ");
  return `${check.warning}. ${shown}${all.length > 8 ? `; ${all.length - 8} more` : ""}`.slice(0, 2000);
}

/** Whether sending should warn: real differences, and not the very ones staff already went through (po.plan_check_key). */
export const needsWarning = (check: DraftCheck, seenKey: string | undefined) => check.applies && check.key !== "" && check.key !== (seenKey ?? "");

/** What Update selected changes: line quantities (with their new snapshots), lines off, lines added. */
export type PlanChanges = {
  updates: { line_id: string; qty: number; plan_basis: PlanBasis }[];
  removes: string[];
  adds: { id: string; item_id: string; qty: number; plan_basis: PlanBasis }[];
  /** The differences left as they are (unticked), which sending won't warn about again. */
  key: string;
};

/** One ticked item for po.update_from_plan, with its lines as the check saw them (a change since is refused). */
export type UpdateItem = { item_id: string; action: "set" | "remove" | "add"; lines: { id: string; qty: number }[]; line_id?: string };

/**
 * The changes for the ticked items, from a check: the plan's quantity (an item on several lines changes its last one,
 * whose snapshot keeps that line's own qty), not-needed lines off, additions on (`lineIdFor`: a new line's id). The
 * browser shows them at once; the server works them out again from its own check before applying them.
 */
export function planChanges(check: DraftCheck, poId: string, ticked: ReadonlySet<string>, lineIdFor: (itemId: string) => string): PlanChanges {
  const updates: PlanChanges["updates"] = [];
  const removes: string[] = [];
  for (const row of check.rows) {
    if (!ticked.has(row.itemId) || !row.action || !row.result) continue;
    if (row.action === "remove") {
      removes.push(...row.lineIds);
      continue;
    }
    const last = row.lines[row.lines.length - 1];
    const qty = r4(last.qty + (row.plan ?? 0) - row.drafted);
    if (qty > 0) updates.push({ line_id: last.id, qty, plan_basis: planBasis(row.result, check.today, { via: "check", qty, poId }) });
  }
  const adds = check.additions
    .filter((a) => ticked.has(a.itemId))
    .map((a) => ({ id: lineIdFor(a.itemId), item_id: a.itemId, qty: a.qty, plan_basis: planBasis(a.result, check.today, { via: "check", poId }) }));
  const left = check.rows.filter((r) => !ticked.has(r.itemId) || !r.action);
  return {
    updates,
    removes,
    adds,
    key: checkKey(
      left,
      check.additions.filter((a) => !ticked.has(a.itemId)),
    ),
  };
}

/** po.update_from_plan's items for the ticked rows and additions (each with its lines as this check saw them). */
export function updateItems(check: DraftCheck, ticked: ReadonlySet<string>, lineIdFor: (itemId: string) => string): UpdateItem[] {
  const rows: UpdateItem[] = check.rows
    .filter((r) => ticked.has(r.itemId) && r.action)
    .map((r) => ({ item_id: r.itemId, action: r.action as "set" | "remove", lines: r.lines }));
  const adds: UpdateItem[] = check.additions
    .filter((a) => ticked.has(a.itemId))
    .map((a) => ({ item_id: a.itemId, action: "add", lines: [], line_id: lineIdFor(a.itemId) }));
  return [...rows, ...adds];
}
